import { apiErrorMessage } from '@agos/api-client'
import {
  useGetBurgerBoxConfig,
  useGetCategories,
  useGetProducts,
  usePutBurgerBoxConfig,
} from '@agos/api-client/menu'
import { T, tt } from '@agos/shared'
import {
  ConfirmDialog,
  DataTable,
  FilterBar,
  PageHeader,
  StatusBadge,
  cn,
  toast,
  type Column, BUTTON_PRIMARY} from '@agos/ui'
import { Box, Info, Loader2, PackageCheck, Plus, Save, ToggleLeft, ToggleRight } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { BoxEditor } from './BoxEditor'
import { newBurgerBox, parseBurgerBoxConfig, type BurgerBoxItem } from './types'

/**
 * Ported from legacy `pages/burger-box.tsx`. Spec:
 * `docs/pages/manager-menu/burger-box.md`.
 *
 * Mounted at **`/kombo-box`**, not `/burger-box` — the page file and the route
 * disagree in legacy, and the route is what users have bookmarked.
 *
 * Everything is edited locally and written in one PUT. Legacy loses the lot on
 * navigation with no warning (FB-020) and hides a failed save in the console
 * (FB-021); both are fixed here.
 */

const PAGE_SIZE = 10
const SAVED_BADGE_MS = 2500

export function BurgerBoxPage() {
  const { data: config, isLoading } = useGetBurgerBoxConfig()
  const { data: categories } = useGetCategories()
  const { data: products } = useGetProducts()
  const saveConfig = usePutBurgerBoxConfig()

  const [items, setItems] = useState<BurgerBoxItem[]>([])
  /** What the server last confirmed, so "changed" is answerable. */
  const [savedSnapshot, setSavedSnapshot] = useState('[]')
  const [justSaved, setJustSaved] = useState(false)
  const [search, setSearch] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [pendingDeletion, setPendingDeletion] = useState<BurgerBoxItem | null>(null)

  // Seed the editor from whatever the server answered, once, using React's
  // sanctioned "adjust state when the input changed" pattern rather than an
  // effect — an effect here would render the empty editor first and then
  // replace it, which reads as a flicker on a slow connection.
  const [seededFrom, setSeededFrom] = useState<unknown>(undefined)
  if (config !== undefined && config !== seededFrom) {
    const parsed = parseBurgerBoxConfig(config)
    setSeededFrom(config)
    setItems(parsed)
    setSavedSnapshot(JSON.stringify(parsed))
  }

  const hasUnsavedChanges = JSON.stringify(items) !== savedSnapshot

  // FB-020: ten minutes of configuration disappear on a stray click, because
  // nothing is written until Uložiť and legacy never warns.
  useEffect(() => {
    if (!hasUnsavedChanges) return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [hasUnsavedChanges])

  const updateItem = (itemId: string, patch: Partial<BurgerBoxItem>) =>
    setItems((current) =>
      current.map((item) => (item.id === itemId ? { ...item, ...patch } : item)),
    )

  const save = () => {
    saveConfig.mutate(
      { data: items as unknown as Record<string, unknown> },
      {
        onSuccess: () => {
          setSavedSnapshot(JSON.stringify(items))
          setJustSaved(true)
          setTimeout(() => setJustSaved(false), SAVED_BADGE_MS)
        },
        // FB-021: legacy logs this to the console and stops the spinner. The
        // only clue is the confirmation that never appears.
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Uloženie zlyhalo'),
            description: apiErrorMessage(caught, tt('Nastavenia sa nepodarilo uložiť.')),
          }),
      },
    )
  }

  const categoryName = (categoryId: number | null) =>
    categoryId ? ((categories ?? []).find((entry) => entry.id === categoryId)?.name ?? '-') : '-'

  const visibleItems = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return items
    return items.filter((item) => item.name.toLowerCase().includes(query))
  }, [items, search])

  const columns: Column<BurgerBoxItem>[] = [
    {
      key: 'name',
      header: tt('Názov'),
      sortable: true,
      sortValue: (row) => row.name.toLowerCase(),
      render: (row) => (
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0',
              row.active ? 'bg-amber-500/15' : 'bg-muted',
            )}
          >
            <Box
              className={cn('w-3.5 h-3.5', row.active ? 'text-amber-500' : 'text-muted-foreground')}
            />
          </div>
          <span className="font-semibold truncate">{row.name || tt('Box bez názvu')}</span>
        </div>
      ),
    },
    {
      key: 'category',
      header: tt('Kategória'),
      sortable: true,
      sortValue: (row) => categoryName(row.triggerCategoryId).toLowerCase(),
      render: (row) => (
        <span className="text-muted-foreground">{categoryName(row.triggerCategoryId)}</span>
      ),
    },
    {
      key: 'slots',
      header: tt('Sloty'),
      sortable: true,
      sortValue: (row) => row.slots.length,
      render: (row) => <span className="text-muted-foreground">{row.slots.length}</span>,
    },
    {
      key: 'status',
      header: tt('Stav'),
      sortable: true,
      sortValue: (row) => (row.active ? 1 : 0),
      render: (row) => (
        <StatusBadge
          label={row.active ? tt('Aktívny') : tt('Neaktívny')}
          color={row.active ? 'green' : 'zinc'}
          icon={row.active ? ToggleRight : ToggleLeft}
        />
      ),
    },
  ]

  const saveButton = (label: string) => (
    <button
      type="button"
      onClick={save}
      disabled={saveConfig.isPending}
      className={cn(
        'flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all',
        justSaved
          ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
          : 'bg-primary text-primary-foreground font-bold hover:opacity-90 transition-opacity disabled:opacity-50',
      )}
    >
      {saveConfig.isPending ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : justSaved ? (
        <PackageCheck className="w-4 h-4" />
      ) : (
        <Save className="w-4 h-4" />
      )}
      {justSaved ? tt('Uložené!') : label}
    </button>
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const expandedItem = items.find((item) => item.id === expandedId)

  return (
    <div className="space-y-6 pb-10">
      <PageHeader
        title="Kombo BOX"
        description={tt('Konfigurácia kombinácie na web')}
        icon={<Box className="w-5 h-5 text-amber-500" />}
        actions={
          <>
            {hasUnsavedChanges && (
              <span className="text-xs font-semibold text-amber-500 whitespace-nowrap">
                <T>Neuložené zmeny</T>
              </span>
            )}
            <button
              type="button"
              onClick={() => setItems((current) => [...current, newBurgerBox()])}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-all border border-border"
            >
              <Plus className="w-4 h-4" /> <T>Nový Kombo BOX</T>
            </button>
            {saveButton(tt('Uložiť'))}
          </>
        }
      />

      {items.length > 0 && (
        <div className="flex items-start gap-2 bg-amber-500/8 border border-amber-500/20 rounded-xl px-4 py-3 text-xs text-amber-600">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>
            <T>
              Každý Kombo BOX sa zobrazí zákazníkovi automaticky, keď otvorí svoju nastavenú
              kategóriu. Zmeny sa prejavia po kliknutí na
            </T>{' '}
            <strong>
              <T>Uložiť</T>
            </strong>
            .
          </span>
        </div>
      )}

      {items.length === 0 && (
        <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl">
          <div className="w-14 h-14 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Box className="w-7 h-7 text-amber-500" />
          </div>
          <h3 className="text-lg font-bold text-foreground mb-1">
            <T>Žiadne Kombo BOXy</T>
          </h3>
          <p className="text-sm text-muted-foreground mb-5 max-w-xs mx-auto">
            <T>
              Vytvorte prvý Kombo BOX - zákazník si bude môcť zostaviť kombináciu z rôznych kategórií.
            </T>
          </p>
          <button
            type="button"
            onClick={() => setItems((current) => [...current, newBurgerBox()])}
            className={cn('inline-flex items-center gap-2 px-5 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            <Plus className="w-4 h-4" /> <T>Vytvoriť prvý Kombo BOX</T>
          </button>
        </div>
      )}

      {items.length > 0 && (
        <>
          <FilterBar
            search={{
              value: search,
              onChange: setSearch,
              placeholder: tt('Hľadať box podľa názvu...'),
            }}
          />

          <DataTable<BurgerBoxItem>
            columns={columns}
            data={visibleItems}
            rowKey={(row) => row.id}
            pageSize={PAGE_SIZE}
            emptyMessage={tt('Žiadne boxy zodpovedajúce vyhľadávaniu')}
            onRowClick={(row) =>
              setExpandedId((current) => (current === row.id ? null : row.id))
            }
          />

          {expandedItem && (
            <BoxEditor
              item={expandedItem}
              categories={categories ?? []}
              products={products ?? []}
              onChange={(patch) => updateItem(expandedItem.id, patch)}
              onDelete={() => setPendingDeletion(expandedItem)}
            />
          )}

          <div className="flex justify-end">{saveButton(tt('Uložiť nastavenia'))}</div>
        </>
      )}

      {/* FB-023: legacy removes a box and all its slots on one click. */}
      <ConfirmDialog
        open={pendingDeletion !== null}
        onClose={() => setPendingDeletion(null)}
        onConfirm={() => {
          if (!pendingDeletion) return
          setItems((current) => current.filter((item) => item.id !== pendingDeletion.id))
          setExpandedId(null)
          setPendingDeletion(null)
        }}
        variant="danger"
        title={tt('Odstrániť Kombo BOX?')}
        description={`${tt('Box')} "${pendingDeletion?.name ?? ''}" ${tt('sa odstráni aj so svojimi slotmi. Zmena sa uloží až po kliknutí na Uložiť.')}`}
        confirmLabel={tt('Odstrániť')}
      />
    </div>
  )
}
